import {IConfigurationService} from "./configuration.service.interface";
import {IConfigurationObject} from "./configuration-object.interface";
import {makeObservable, observable, reaction, runInAction} from "mobx";
import {ServiceBase} from "../service-base";
import {IServiceFactory} from "../service-factory.interface";
import {TimeSpan} from "../../types/time-span";
import {EnvConfigs} from "../../env.configs";
import {Check} from "../../types/type-checking";

const REGULAR_CONFIGS_REFRESH_INTERVAL = TimeSpan.fromMinutes(5);
const ACCELERATED_CONFIGS_REFRESH_INTERVAL = TimeSpan.fromSeconds(5);

//https://www.youtube.com/watch?v=NJwBNWwNlTk

export class ConfigurationService extends ServiceBase implements IConfigurationService {

    constructor(services: IServiceFactory) {
        super(services);

        this.envConfigs = new EnvConfigs();

        makeObservable(this, {
            data: observable
        });

        reaction(() => this.services.language.currentLanguage,
            async () => {
                await this.loadConfigurations();
            });

        setTimeout(() => {
            this.services.application.sendEnvironmentTypeEvent(this.envConfigs.currentEnvironmentType);
        })
    }

    readonly envConfigs: EnvConfigs;


    get airlineWebapiUrl(): string {
        return this.envConfigs.airlineWebapiOrigin;
    }



    get dotRezApiUrl(): string {
        return this.data.dotRezApiUrl;
    }

    get dotRezApiSubscriptionKeyHeader(): Record<string, string> {

        if(Check.isEmpty(this.data.dotRezApiSubscriptionKey)) {
            return {};
        }

        return {
            "Ocp-Apim-Subscription-Key": this.data.dotRezApiSubscriptionKey
        }


    }

    get dotRezSessionTimeout(): TimeSpan {
        return TimeSpan.parse(this.data.dotRezSessionTimeout);
    }

    data: IConfigurationObject = {
        dotRezApiUrl: "",
        dotRezApiSubscriptionKey: "",
        bookingEngineUrl: "",
        dotRezSessionTimeout: "00:15:00",
        refreshConfigsInterval: "01:00:00",
        maxAllowedPassengersNumberOnBooking: 9,
        hidePricesInSearchMask: false,
        countries: [],
        stations: [],
        macs: [],
        psoMarketsV2: {
            classesOfService: {
                forResident: [],
                forWorker: [],
                forFamily: []
            },
            markets: []
        },
        supportedLanguages: [],
        supportedCurrencies: [],
        forbiddenVouchers: [],
        marketing: {
            enableLowFare: true
        },
        cms: {
            baseUrl: "",
            pages: {
                pso: ""
            }
        }
    };

    private _refreshConfigsInterval: any = null;
    private _startRefreshConfigsTimer(refreshInterval?: TimeSpan) {
        if(!refreshInterval) {
            refreshInterval =  this.data.refreshConfigsInterval
                ? TimeSpan.parse(this.data.refreshConfigsInterval)
                : REGULAR_CONFIGS_REFRESH_INTERVAL;
        }


        this._refreshConfigsInterval = setInterval(async () => {
            await this.loadConfigurations();
        }, refreshInterval.totalMilliseconds);
    }

    private _stopRefreshConfigsTimer() {
        if(this._refreshConfigsInterval) {
            clearInterval(this._refreshConfigsInterval);
            this._refreshConfigsInterval = null;
        }
    }

    async loadConfigurations(): Promise<void> {
        this._stopRefreshConfigsTimer();
        try {
            const response = await this.services.airlineWebapi.getConfigurations(this.services.language.currentLanguage);

            runInAction(() => {
                this.data = response.configurations;
            });

            this._startRefreshConfigsTimer();

        } catch (err) {
            this.services.logger.error('Failed to load configurations', err);
            this._startRefreshConfigsTimer(ACCELERATED_CONFIGS_REFRESH_INTERVAL);
        }
    }

    get isProduction(): boolean {
        return this.envConfigs.isProduction;
    }

}
